// Intentional cross-module imports — AnnotationViewerView is the bridge
// between docs domain (annotation data) and viewers domain (rendering).
// It is the only permitted docs/annotations → viewers boundary crossing.
import { memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useGetDocFile } from "@modules/docs/documents/hooks";
import { ViewerHeader } from "@modules/viewers/components/ViewerHeader";
import { ViewerSkeleton } from "@modules/viewers/components/ViewerSkeleton";
import { ViewerContentSkeleton } from "@modules/viewers/components/ViewerContentSkeleton";
import { useViewerTranslate } from "@modules/viewers/hooks/useTranslate";
import { PdfViewer } from "@modules/viewers/pdf/components";
import { Button } from "@core/components";
import { icons } from "@core/assets";
import {
  ApprovalInfo,
  AnnotationToolbar,
  ShapeOverlay,
  useAnnotationsVM,
  useGetApprovalAnnotations,
  useCreateApprovalAnnotations,
} from "@modules/docs/annotations";
import type { SaveAnnotationsPayload } from "@modules/docs/annotations";
import AnnotationComment from "@modules/docs/annotations/components/AnnotationComment";
import type { ShapeAnnotation, RGBA } from "@modules/docs/annotations/types";
import type { PdfPageMetrics } from "@core/types";

// ─── Save payload builder ─────────────────────────────────────────────────────

function buildSavePayload(
  current: ShapeAnnotation[],
  initial: ShapeAnnotation[],
): SaveAnnotationsPayload {
  const initialIds = new Set(initial.map((s) => s.id));
  const currentIds = new Set(current.map((s) => s.id));

  const create_annotations = current
    .filter((s) => s.source === "local")
    .map((s) => ({
      type: s.type,
      kind: s.kind,
      color: s.color,
      areas: s.areas.map((a) => ({ page: a.page, geom: a.position })),
    }));

  const update_annotations = current
    .filter((s) => s.source === "backend" && initialIds.has(s.id))
    .filter((s) => {
      const orig = initial.find((i) => i.id === s.id);
      if (!orig) return false;
      return (
        JSON.stringify(s.areas) !== JSON.stringify(orig.areas) ||
        s.color !== orig.color
      );
    })
    .map((s) => ({
      annotation_id: s.id,
      type: s.type,
      kind: s.kind,
      color: s.color,
      areas: s.areas.map((a) => ({ page: a.page, geom: a.position })),
    }));

  const delete_annotations = initial
    .filter((s) => !currentIds.has(s.id))
    .map((s) => s.id);

  return { create_annotations, update_annotations, delete_annotations };
}

// ─── ViewerLayout ─────────────────────────────────────────────────────────────

const ViewerLayout = memo(function ViewerLayout({
  header,
  content,
}: {
  header: React.ReactNode;
  content: React.ReactNode;
}) {
  return (
    <div className="flex h-screen w-full flex-col">
      {header}
      <div className="flex min-h-0 flex-1 overflow-hidden">{content}</div>
    </div>
  );
});

// ─── View ─────────────────────────────────────────────────────────────────────

export function AnnotationViewerView() {
  const { docId } = useParams<{ docId: string }>();
  const [searchParams] = useSearchParams();
  const { data, isLoading, isError } = useGetDocFile(docId!);
  const navigate = useNavigate();
  const { t } = useViewerTranslate();

  const isAnnotationMode =
    searchParams.get("annotation") === "true" &&
    !!searchParams.get("approval_id");
  const canAnnotate = isAnnotationMode
    ? searchParams.get("readonly") !== "true"
    : false;
  const canComment = isAnnotationMode
    ? searchParams.get("nocomment") !== "true"
    : false;

  const onClose = useCallback(() => navigate(-1), [navigate]);
  const [isApprovalInfoOpen, setIsApprovalInfoOpen] = useState(false);

  // ── Annotation state ────────────────────────────────────────────────────────
  const [shapes, setShapes] = useState<ShapeAnnotation[]>([]);
  const [initialShapes, setInitialShapes] = useState<ShapeAnnotation[]>([]);
  const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);
  const [isCommentMenuOpen, setIsCommentMenuOpen] = useState(false);

  // ── Annotation hooks ────────────────────────────────────────────────────────
  const {
    activeTool,
    selectedShape,
    changeShape,
    selectedShapeColor,
    changeShapeColor,
  } = useAnnotationsVM();

  const { data: backendAnnotations } = useGetApprovalAnnotations();
  const saveAnnotationsMutation = useCreateApprovalAnnotations();

  // Sync backend annotations into local state once loaded
  useEffect(() => {
    if (!backendAnnotations) return;
    setShapes(backendAnnotations);
    setInitialShapes(backendAnnotations);
    setSelectedShapeId(null);
  }, [backendAnnotations]);

  // Apply color change to selected shape
  const prevColorRef = useRef<RGBA>(selectedShapeColor);
  useEffect(() => {
    if (prevColorRef.current === selectedShapeColor) return;
    prevColorRef.current = selectedShapeColor;
    if (!selectedShapeId) return;
    setShapes((prev) =>
      prev.map((s) =>
        s.id === selectedShapeId ? { ...s, color: selectedShapeColor } : s,
      ),
    );
  }, [selectedShapeColor, selectedShapeId]);

  // ── Derived state ───────────────────────────────────────────────────────────
  const hasChanges = useMemo(() => {
    if (shapes.length !== initialShapes.length) return true;
    const initialMap = new Map(initialShapes.map((s) => [s.id, s]));
    return shapes.some((s) => {
      if (s.source === "local") return true;
      const orig = initialMap.get(s.id);
      if (!orig) return true;
      return (
        JSON.stringify(s.areas) !== JSON.stringify(orig.areas) ||
        s.color !== orig.color
      );
    });
  }, [shapes, initialShapes]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    const payload = buildSavePayload(shapes, initialShapes);
    saveAnnotationsMutation.mutate(payload, {
      onSuccess: (saved) => {
        setShapes(saved);
        setInitialShapes(saved);
        setSelectedShapeId(null);
      },
    });
  }, [shapes, initialShapes, saveAnnotationsMutation]);

  const handleRequestComment = useCallback(
    (id: string) => {
      setSelectedShapeId(id);
      setIsCommentMenuOpen(true);
    },
    [],
  );

  // ── Render props for PdfViewer ──────────────────────────────────────────────
  const renderAnnotationToolbar = useCallback(
    (_pageMetrics: PdfPageMetrics) =>
      isAnnotationMode ? (
        <AnnotationToolbar
          selectedShape={selectedShape}
          onShapeChange={changeShape}
          selectedColor={selectedShapeColor}
          onColorChange={changeShapeColor}
          canAnnotate={canAnnotate}
          onSave={handleSave}
          isSaving={saveAnnotationsMutation.isPending}
          hasChanges={hasChanges}
        />
      ) : null,
    [
      isAnnotationMode,
      selectedShape,
      changeShape,
      selectedShapeColor,
      changeShapeColor,
      canAnnotate,
      handleSave,
      saveAnnotationsMutation.isPending,
      hasChanges,
    ],
  );

  const renderShapeOverlay = useCallback(
    (pageMetrics: PdfPageMetrics, scale: number, totalPages: number) => (
      <ShapeOverlay
        pageMetrics={pageMetrics}
        totalPages={totalPages}
        scale={scale}
        shapes={shapes}
        onShapesChange={setShapes}
        selectedShapeId={selectedShapeId}
        onSelectShape={setSelectedShapeId}
        activeTool={activeTool}
        shapeKind={selectedShape}
        color={selectedShapeColor}
        canAnnotate={canAnnotate}
        onRequestComment={handleRequestComment}
      />
    ),
    [
      shapes,
      selectedShapeId,
      activeTool,
      selectedShape,
      selectedShapeColor,
      canAnnotate,
      handleRequestComment,
    ],
  );

  // ── Early returns ───────────────────────────────────────────────────────────
  if (isLoading) return <ViewerSkeleton />;

  if (isError || !data) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <span className="text-sm text-neutral-500">{t("load-error")}</span>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  const tools = (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      leftIcon={icons["info"]}
      iconStrokeWidth={1.5}
      onClick={() => setIsApprovalInfoOpen(true)}
    />
  );

  const header = <ViewerHeader doc={data} onClose={onClose} tools={tools} />;

  const content = (
    <Suspense fallback={<ViewerContentSkeleton />}>
      {data.format.toLowerCase() === "pdf" ? (
        <PdfViewer
          url={data.url}
          name={data.name}
          size={data.size}
          renderAnnotationToolbar={renderAnnotationToolbar}
          renderShapeOverlay={renderShapeOverlay}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <span className="text-sm text-neutral-500">
            {t("format-not-supported")}
          </span>
        </div>
      )}
    </Suspense>
  );

  return (
    <>
      <ViewerLayout header={header} content={content} />
      {isCommentMenuOpen && (
        <AnnotationComment
          annotationId={selectedShapeId}
          onClose={() => setIsCommentMenuOpen(false)}
          readOnly={!canComment}
        />
      )}
      {isApprovalInfoOpen && (
        <ApprovalInfo
          onClose={() => setIsApprovalInfoOpen(false)}
          canAnnotate={canAnnotate}
        />
      )}
    </>
  );
}
